import { useSyncExternalStore } from 'react';
import { X } from '../../../icons';
import { Button } from '../../../components/shared';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { TargetPicker, type TargetRow } from '../TargetPicker';
import { tBoards, getBoardsLang, subscribeBoardsLang } from '../../../lib/boards-i18n';

/**
 * 대상 고르기 창 — 열람자·알림 공용 (2026-08-18).
 *
 * 고르는 일 자체는 기존 `TargetPicker` 가 그대로 한다(그 파일은 **한 줄도 안 고친다**).
 * 여기서는 좁은 우측 패널에서 빼내 넓은 자리에 앉히는 껍데기만 만든다 —
 * 저장 규약(`targetType: company|division|team|user` + `targetId`, 전사는 0)도 그대로다.
 *
 * 고른 값은 **곧바로 반영된다**(onChange 를 그대로 넘긴다). 그래서 닫기 하나뿐이고
 * 취소는 없다 — 잘못 담았으면 칩의 × 로 뺀다.
 */

interface BoardTargetModalProps {
  title: string;
  value: TargetRow[];
  onChange: (next: TargetRow[]) => void;
  /** 알림 체크박스 노출 여부. 열람자 지정에서는 필요 없다. */
  showNotify?: boolean;
  onClose: () => void;
}

export function BoardTargetModal({ title, value, onChange, showNotify = false, onClose }: BoardTargetModalProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  useEscapeClose(onClose);

  return (
    <div className="bd-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        ref={trapRef}
        className="bd-modal bd-modal--sm"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="bd-modal__head">
          <h2>{title}</h2>
          <Button
            variant="ghost" size="sm" iconOnly
            onClick={onClose}
            aria-label={tBoards('boards.btn.close')}
            iconLeft={<X size={16} aria-hidden />}
          />
        </header>

        <div className="bd-modal__body">
          <TargetPicker value={value} onChange={onChange} showNotify={showNotify} />
        </div>

        <footer className="bd-modal__foot">
          <span className="bd-spacer" />
          <Button variant="primary"
            onClick={onClose}
          >
            {tBoards('boards.btn.close')}
          </Button>
        </footer>
      </div>
    </div>
  );
}
